import { Autocomplete, IconButton, InputAdornment, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridToolbar,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import { AlertComponent, DialogComponent } from "../../components";
import { useDate, useFetch, usePermissions } from "../../hooks";
import {
  ClientProps,
  TechListProps,
  WellProps,
} from "../../interfaces/interfaces";
import EditIcon from "@mui/icons-material/Edit";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import SellIcon from "@mui/icons-material/Sell";
import PrintIcon from "@mui/icons-material/Print";
import DeleteIcon from "@mui/icons-material/Delete";
import DifferenceIcon from "@mui/icons-material/Difference";
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Dayjs } from "dayjs";
import { Link, useNavigate } from "react-router-dom";
import { useContext, useEffect, useMemo, useState } from "react";
import DataContext from "../../context/DataContext";
import PaginatorContext from "../../context/PaginatorContext";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { NavBar } from "../../components/layout/Navbar";
import {
  TextField,
  FormControl,
  Box,
  Paper,
  Container,
  Fade,
  Grid,
} from "@mui/material";
import { ClientSelector, WellSelector } from "../../components/basicInfo";
import {
  modernDataGridStyles,
  modernIconButtonStyles,
} from "../../utils/dataGridStyles";

interface FetchResponse {
  success?: boolean;
  data: TechListProps[];
}

export const TechDesignList = () => {
  const navigate = useNavigate();
  const matches = useMediaQuery("(min-width:600px)");
  const theme = useTheme();
  const { paginationModel, fetchPaginationModel } =
    useContext(PaginatorContext);

  const {
    onResetValues,
    fetchDataTechProp,
    onDeleteProp,
    isSuccess,
    isError,
    clientOptions,
    wellOptions,
    fetchDataWells,
    fetchDataClient,
  } = useContext(DataContext);
  const { getDateFromString } = useDate();
  const { canManageDesigns } = usePermissions();
  const [idToDelete, setIdToDelete] = useState("");
  const [isOpenAlert, setIsOpenAlert] = useState(false);
  const [tableData, setTableData] = useState<TechListProps[] | []>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState<ClientProps | null>(
    null,
  );
  const [selectedWell, setSelectedWell] = useState<WellProps | null>(null);
  const [createdBy, setCreatedBy] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const [userOptions, setUserOptions] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    const tokenInfo = localStorage.getItem("info");
    fetch(`${process.env.REACT_APP_DEV_API}/user`, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tokenInfo}` },
    })
      .then((res) => res.json())
      .then((res) => { if (res.success && res.data) setUserOptions(res.data.filter((u: any) => u.active && u.name)); })
      .catch(() => {});
  }, []);

  const filters = useMemo(
    () => ({
      search: searchTerm,
      client: selectedClient?.id,
      well: selectedWell?.id,
      createdBy: createdBy || undefined,
      dateFrom: dateFrom ? dateFrom.format("YYYY-MM-DD") : undefined,
      dateTo: dateTo ? dateTo.format("YYYY-MM-DD") : undefined,
    }),
    [searchTerm, selectedClient, selectedWell, createdBy, dateFrom, dateTo],
  );
  const { data, isLoading, fetchData, pagination } = useFetch<FetchResponse>(
    "proposal-detail",
    paginationModel,
    undefined,
    undefined,
    filters,
  );

  useEffect(() => {
    fetchDataClient();
  }, []);

  // Reset to page 0 whenever filters change so results are always visible
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
  }, [searchTerm, selectedClient, selectedWell, createdBy, dateFrom, dateTo]); // intentionally omit fetchPaginationModel to avoid infinite loop

  useEffect(() => {
    data && data.data && setTableData(data.data);
  }, [data]);

  const columns: GridColDef[] = [
    {
      field: "client",
      headerName: "Client",
      renderHeader: () => <strong>{"CLIENT"}</strong>,
      sortable: true,
      width: 180,
      valueGetter: (params: GridValueGetterParams) =>
        params.row?.well?.client?.name || "",
      renderCell: (params: any) => {
        return (
          <Link
            to={`/tech/${params.row?.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {params.row?.well?.client?.name}
          </Link>
        );
      },
    },
    {
      field: "well",
      headerName: "Well",
      renderHeader: () => <strong>{"WELL"}</strong>,
      sortable: true,
      width: 180,
      valueGetter: (params: GridValueGetterParams) =>
        params.row?.well.name || "",
    },
    {
      field: "customName",
      headerName: "Description",
      renderHeader: () => <strong>{"DESCRIPTION"}</strong>,
      width: 180,
    },
    {
      field: "date",
      headerName: "Created At",
      renderHeader: () => <strong>{"CREATED AT"}</strong>,
      width: 110,
      valueGetter: (params: GridValueGetterParams) =>
        getDateFromString(params.row?.date),
    },
    {
      field: "createdBy",
      headerName: "Created By",
      renderHeader: () => <strong>{"CREATED BY"}</strong>,
      width: 150,
      valueGetter: (params: GridValueGetterParams) =>
        params.row?.createdBy || "—",
    },
    {
      field: "action",
      headerName: "Actions",
      renderHeader: () => <strong>{"ACTIONS"}</strong>,
      width: 280,
      filterable: false,
      sortable: false,
      renderCell: (params: any) => {
        return (
          <Box className="action-buttons" sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Edit">
              <IconButton
                onClick={() => navigate(`/tech/${params.row?.id}`)}
                sx={modernIconButtonStyles(theme)}
                size="small"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canManageDesigns && (
              <Tooltip title="Duplicate">
                <IconButton
                  onClick={() => {
                    fetchDataTechProp(params.row?.id);
                    navigate("/tech/0");
                  }}
                  sx={modernIconButtonStyles(theme)}
                  size="small"
                >
                  <FileCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canManageDesigns && (
              <Tooltip title="Create as Tally">
                <IconButton
                  onClick={() => {
                    fetchDataTechProp(params.row?.id, true);
                    navigate("/tally/0");
                  }}
                  sx={modernIconButtonStyles(theme)}
                  size="small"
                >
                  <DifferenceIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canManageDesigns && (
              <Tooltip title="Generate Sales">
                <IconButton
                  onClick={() => navigate(`/sales/tech/${params.row?.id}`)}
                  sx={modernIconButtonStyles(theme)}
                  size="small"
                >
                  <SellIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Print">
              <IconButton
                component={Link}
                to={`/tech/${params.row?.id}?pdf=true`}
                sx={modernIconButtonStyles(theme)}
                size="small"
              >
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canManageDesigns && (
              <Tooltip title="Delete">
                <IconButton
                  onClick={() => {
                    setIsOpenAlert(true);
                    setIdToDelete(params.row?.id);
                  }}
                  sx={modernIconButtonStyles(theme)}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "lastUpdatedAt",
      headerName: "Last Update",
      renderHeader: () => <strong>{"LAST UPDATE"}</strong>,
      width: 110,
      valueGetter: (params: GridValueGetterParams) =>
        params.row?.lastUpdatedAt
          ? getDateFromString(params.row.lastUpdatedAt)
          : "—",
    },
    {
      field: "lastUpdatedBy",
      headerName: "Last Update By",
      renderHeader: () => <strong>{"LAST UPDATE BY"}</strong>,
      width: 150,
      valueGetter: (params: GridValueGetterParams) =>
        params.row?.lastUpdatedBy || "—",
    },
  ];

  const buttons = canManageDesigns
    ? [
        {
          title: "New",
          action: () => {
            onResetValues();
            navigate("/tech/0");
          },
          icon: <AddBoxIcon />,
        },
      ]
    : [];

  return (
    <>
      <NavBar title="Technical Proposals" buttons={buttons} />
      <Box
        sx={{
          marginTop: "80px",
          minHeight: "calc(100vh - 80px)",
          background:
            "linear-gradient(135deg, rgba(241, 236, 231, 0.8) 0%, rgba(241, 236, 231, 0.4) 100%)",
          padding: { xs: 2, md: 4 },
        }}
      >
        <Container maxWidth="xl">
          <Fade in={true} timeout={600}>
            <Paper
              elevation={3}
              sx={{
                padding: { xs: 2, md: 4 },
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(140, 150, 169, 0.1)",
                boxShadow: "0 8px 32px rgba(140, 150, 169, 0.15)",
              }}
            >
              {/* Search and Filter Section */}
              <Box sx={{ mb: 3 }}>
                {/* All inputs in one row */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      placeholder="Search by name..."
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: "rgba(140, 150, 169, 0.7)", fontSize: "1.1rem" }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "rgba(255, 255, 255, 0.8)",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "white",
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        width: "100% !important",
                        maxWidth: "100% !important",
                        "& .MuiAutocomplete-root": {
                          width: "100% !important",
                          maxWidth: "100% !important",
                          "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
                            top: "50%",
                            transform: "translate(14px, -50%) scale(1)",
                          },
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor: "rgba(255, 255, 255, 0.8)",
                            padding: "0 !important",
                            minHeight: "40px",
                            width: "100% !important",
                            maxWidth: "100% !important",
                            boxSizing: "border-box !important",
                            "&:hover": {
                              backgroundColor: "rgba(255, 255, 255, 0.9)",
                            },
                            "&.Mui-focused": {
                              backgroundColor: "white",
                            },
                          },
                          "& .MuiAutocomplete-input": {
                            padding: "8.5px 14px !important",
                            width: "100% !important",
                            maxWidth: "100% !important",
                            boxSizing: "border-box !important",
                          },
                          "& .MuiAutocomplete-endAdornment": {
                            right: "9px !important",
                          },
                        },
                        "& .MuiFormControl-root": {
                          width: "100% !important",
                          minWidth: "unset !important",
                          maxWidth: "100% !important",
                        },
                      }}
                    >
                      <ClientSelector
                        options={clientOptions}
                        value={selectedClient as any}
                        handleChangeClient={(newValue: ClientProps | null) => {
                          setSelectedClient(newValue);
                          setSelectedWell(null);
                          newValue && fetchDataWells(newValue?.id);
                        }}
                        onClear={() => {
                          setSelectedClient(null);
                          setSelectedWell(null);
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        width: "100% !important",
                        maxWidth: "100% !important",
                        "& .MuiAutocomplete-root": {
                          width: "100% !important",
                          maxWidth: "100% !important",
                          "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
                            top: "50%",
                            transform: "translate(14px, -50%) scale(1)",
                          },
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor: "rgba(255, 255, 255, 0.8)",
                            padding: "0 !important",
                            minHeight: "40px",
                            width: "100% !important",
                            maxWidth: "100% !important",
                            boxSizing: "border-box !important",
                            "&:hover": {
                              backgroundColor: "rgba(255, 255, 255, 0.9)",
                            },
                            "&.Mui-focused": {
                              backgroundColor: "white",
                            },
                          },
                          "& .MuiAutocomplete-input": {
                            padding: "8.5px 14px !important",
                            width: "100% !important",
                            maxWidth: "100% !important",
                            boxSizing: "border-box !important",
                          },
                          "& .MuiAutocomplete-endAdornment": {
                            right: "9px !important",
                          },
                        },
                        "& .MuiFormControl-root": {
                          width: "100% !important",
                          minWidth: "unset !important",
                          maxWidth: "100% !important",
                        },
                      }}
                    >
                      <WellSelector
                        options={wellOptions || []}
                        value={selectedWell as any}
                        handleChangeWell={(newValue: WellProps | null) => {
                          setSelectedWell(newValue);
                        }}
                        onClear={() => setSelectedWell(null)}
                      />
                    </FormControl>
                  </Grid>

                  {/* Created By filter */}
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      size="small"
                      options={userOptions}
                      getOptionLabel={(option) => option.name}
                      value={userOptions.find((u) => u.name === createdBy) ?? null}
                      onChange={(_e, newValue) => setCreatedBy(newValue?.name ?? null)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Filter by creator..."
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              backgroundColor: "rgba(255, 255, 255, 0.8)",
                              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.9)" },
                              "&.Mui-focused": { backgroundColor: "white" },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Date From filter */}
                  <Grid item xs={12} sm={4}>
                    <DatePicker
                      label="From"
                      value={dateFrom}
                      onChange={(newValue) => setDateFrom(newValue)}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              backgroundColor: "rgba(255, 255, 255, 0.8)",
                              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.9)" },
                              "&.Mui-focused": { backgroundColor: "white" },
                            },
                          },
                        },
                        field: { clearable: true, onClear: () => setDateFrom(null) },
                      }}
                    />
                  </Grid>

                  {/* Date To filter */}
                  <Grid item xs={12} sm={4}>
                    <DatePicker
                      label="To"
                      value={dateTo}
                      onChange={(newValue) => setDateTo(newValue)}
                      minDate={dateFrom ?? undefined}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              backgroundColor: "rgba(255, 255, 255, 0.8)",
                              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.9)" },
                              "&.Mui-focused": { backgroundColor: "white" },
                            },
                          },
                        },
                        field: { clearable: true, onClear: () => setDateTo(null) },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* DataGrid Section */}
              <Box sx={{ height: "70vh", width: "100%" }}>
                <DataGrid
                  sx={modernDataGridStyles(theme)}
                  density={matches ? "standard" : "compact"}
                  rows={tableData}
                  columns={columns}
                  slots={{ toolbar: GridToolbar }}
                  loading={isLoading}
                  rowCount={+pagination?.totalRecords}
                  paginationMode="server"
                  paginationModel={paginationModel}
                  onPaginationModelChange={fetchPaginationModel}
                />
              </Box>
            </Paper>
          </Fade>
        </Container>
      </Box>

      {isSuccess && <AlertComponent type="success" />}
      {isError && <AlertComponent type="error" />}
      {isOpenAlert && (
        <DialogComponent
          type="error"
          modalContent={
            <div>Are you sure you want to remove this Technical Design?</div>
          }
          onAccept={() => {
            onDeleteProp(idToDelete);
            setIdToDelete("");
            setIsOpenAlert(false);
            fetchData();
          }}
          onCancel={() => {
            setIsOpenAlert(false);
            setIdToDelete("");
          }}
        />
      )}
    </>
  );
};
